import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icons, Button } from '../../shared/ui';
const { Chart: BarChart3, Users, Home, Alert: AlertTriangle, DollarSign, Document: FileText, Activity, TrendingUp, TrendingDown, Building: Building2, CheckCircle, Clock, AlertCircle, Wallet, Receipt } = Icons;
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
import {
  ticketService,
  propertyService,
  financeService,
  contractService,
  userService,
  dashboardService
} from '../../services/apiService';

// Occupancy Rate Widget - Enhanced with Luxury Design
export const OccupancyWidget = ({ timeRange }) => {
  const { data: rooms, isLoading } = useQuery({
    queryKey: ['dashboard-rooms', timeRange?.startMonth],
    queryFn: () => propertyService.getRooms()
  });

  const stats = React.useMemo(() => {
    if (!rooms || !Array.isArray(rooms)) return { rate: 0, occupied: 0, total: 0, vacant: 0, maintenance: 0 };
    const occupied = rooms.filter(room => room.status === 'occupied').length;
    const vacant = rooms.filter(room => room.status === 'vacant').length;
    const maintenance = rooms.filter(room => room.status === 'maintenance').length;
    const total = rooms.length;
    return {
      rate: total > 0 ? Math.round((occupied / total) * 100) : 0,
      occupied,
      total,
      vacant,
      maintenance
    };
  }, [rooms]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4 p-2">
        <div className="h-28 flex justify-center items-center">
          <div className="w-24 h-24 bg-stone-200 rounded-full"></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="h-16 bg-stone-100 rounded-xl"></div>
          <div className="h-16 bg-stone-100 rounded-xl"></div>
          <div className="h-16 bg-stone-100 rounded-xl"></div>
        </div>
      </div>
    );
  }

  // Color based on occupancy rate
  const getOccupancyColor = (rate) => {
    if (rate >= 90) return { bg: 'from-emerald-500 to-emerald-600', text: 'text-emerald-600', ring: 'ring-emerald-500/20' };
    if (rate >= 70) return { bg: 'from-amber-500 to-amber-600', text: 'text-amber-600', ring: 'ring-amber-500/20' };
    return { bg: 'from-red-500 to-red-600', text: 'text-red-600', ring: 'ring-red-500/20' };
  };

  const colors = getOccupancyColor(stats.rate);

  return (
    <div className="space-y-6">
      {/* Main Stat Circle */}
      <div className="flex items-center justify-center py-2">
        <div className={`relative w-32 h-32 rounded-full p-2 bg-gradient-to-br ${colors.bg} shadow-lg shadow-stone-200`}>
          <div className="w-full h-full rounded-full bg-white flex flex-col items-center justify-center shadow-inner">
            <span className={`text-3xl font-bold ${colors.text}`}>{stats.rate}%</span>
            <span className="text-xs text-stone-500 font-medium uppercase tracking-wide">Occupied</span>
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 hover:shadow-md transition-shadow">
          <div className="w-2 h-2 rounded-full bg-emerald-500 mx-auto mb-2 shadow-sm shadow-emerald-200"></div>
          <p className="text-lg font-bold text-stone-700">{stats.occupied}</p>
          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Occupied</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-amber-50/50 border border-amber-100 hover:shadow-md transition-shadow">
          <div className="w-2 h-2 rounded-full bg-amber-500 mx-auto mb-2 shadow-sm shadow-amber-200"></div>
          <p className="text-lg font-bold text-stone-700">{stats.vacant}</p>
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Vacant</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-red-50/50 border border-red-100 hover:shadow-md transition-shadow">
          <div className="w-2 h-2 rounded-full bg-red-500 mx-auto mb-2 shadow-sm shadow-red-200"></div>
          <p className="text-lg font-bold text-stone-700">{stats.maintenance}</p>
          <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Maint.</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2 pt-2">
        <div className="flex justify-between text-xs text-stone-500 font-medium px-1">
          <span>Capacity Usage</span>
          <span>{stats.occupied}/{stats.total} Units</span>
        </div>
        <div className="h-3 bg-stone-100 rounded-full overflow-hidden flex shadow-inner">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 relative"
            style={{ width: `${(stats.occupied / stats.total) * 100}%` }}
          >
            <div className="absolute inset-0 bg-white/20"></div>
          </div>
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-500 relative"
            style={{ width: `${(stats.vacant / stats.total) * 100}%` }}
          />
          <div
            className="h-full bg-gradient-to-r from-red-400 to-red-500 relative"
            style={{ width: `${(stats.maintenance / stats.total) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Revenue Chart Widget - Income, Expense & Revenue Trend with Time Range Support
export const RevenueChartWidget = ({ timeRange }) => {
  const [selectedMonth, setSelectedMonth] = React.useState(null);

  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['dashboard-revenue-analytics', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getRevenueAnalytics({
      startMonth: timeRange?.startMonth,
      endMonth: timeRange?.endMonth
    })
  });

  // Process monthly trend data for Income, Expense, Revenue chart
  const monthlyData = React.useMemo(() => {
    if (!analytics?.monthly_trend) return [];
    return analytics.monthly_trend.map(item => ({
      month: item.month,
      monthLabel: new Date(item.month + '-01').toLocaleString('default', { month: 'short', year: '2-digit' }),
      income: item.confirmed || 0,
      expense: item.expense || 0,
      revenue: item.net_income || (item.confirmed || 0) - (item.expense || 0),
      invoiced: item.invoiced || 0,
      collectionRate: item.collection_rate || 0
    }));
  }, [analytics]);

  // Calculate totals and max for chart scaling (include absolute revenue for scaling)
  const totalIncome = monthlyData.reduce((sum, m) => sum + m.income, 0);
  const totalExpense = monthlyData.reduce((sum, m) => sum + m.expense, 0);
  const totalRevenue = totalIncome - totalExpense;
  const maxAmount = Math.max(
    ...monthlyData.map(m => Math.max(m.income, m.expense, Math.abs(m.revenue))),
    1
  );

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-20 bg-stone-100 rounded-xl"></div>
        <div className="h-40 bg-stone-50 rounded-xl"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading revenue data</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards - Income, Expense, Revenue */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
          <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-1">Income</p>
          <p className="text-lg font-bold text-emerald-700">₫{totalIncome.toLocaleString('vi-VN')}</p>
        </div>
        <div className="p-3 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-100">
          <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider mb-1">Expense</p>
          <p className="text-lg font-bold text-red-600">₫{totalExpense.toLocaleString('vi-VN')}</p>
        </div>
        <div className={`p-3 rounded-xl border ${totalRevenue >= 0
          ? 'bg-teal-50 border-teal-100'
          : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100'}`}>
          <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${totalRevenue >= 0 ? 'text-teal-600' : 'text-amber-600'}`}>
            Revenue
          </p>
          <p className={`text-lg font-bold ${totalRevenue >= 0 ? 'text-teal-700' : 'text-amber-700'}`}>
            {totalRevenue >= 0 ? '' : '-'}₫{Math.abs(totalRevenue).toLocaleString('vi-VN')}
          </p>
        </div>
      </div>

      {/* Trend Chart - 3 Bars per Month: Income, Expense, Revenue */}
      <div className="relative">
        {monthlyData.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-stone-400 text-sm">
            No data available for selected period
          </div>
        ) : (
          <>
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 flex flex-col justify-between text-[9px] text-stone-400" style={{ height: '140px' }}>
              <span>₫{(maxAmount / 1000000).toFixed(1)}M</span>
              <span>₫{(maxAmount / 2000000).toFixed(1)}M</span>
              <span>₫0</span>
            </div>

            {/* Chart */}
            <div className="flex items-end gap-1 pl-10" style={{ height: '140px' }}>
              {monthlyData.map((month, index) => {
                const isSelected = selectedMonth?.month === month.month;
                return (
                  <div
                    key={index}
                    className={`flex-1 flex flex-col items-center group relative min-w-0 h-full cursor-pointer transition-all duration-200 ${isSelected ? 'scale-105' : 'hover:scale-102'}`}
                    onClick={() => setSelectedMonth(isSelected ? null : month)}
                  >
                    {/* Bar container - takes remaining height */}
                    <div className="flex-1 w-full flex items-end gap-[2px] justify-center">
                      {/* Income bar (green) */}
                      <div
                        className={`w-3 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t transition-all duration-300 ${isSelected ? 'from-emerald-600 to-emerald-500 shadow-lg shadow-emerald-500/30' : 'group-hover:from-emerald-600 group-hover:to-emerald-500'}`}
                        style={{ height: `${Math.max((month.income / maxAmount) * 100, 2)}%`, minHeight: month.income > 0 ? '4px' : '2px' }}
                      ></div>
                      {/* Expense bar (red) */}
                      <div
                        className={`w-3 bg-gradient-to-t from-red-500 to-red-400 rounded-t transition-all duration-300 ${isSelected ? 'from-red-600 to-red-500 shadow-lg shadow-red-500/30' : 'group-hover:from-red-600 group-hover:to-red-500'}`}
                        style={{ height: `${Math.max((month.expense / maxAmount) * 100, 2)}%`, minHeight: month.expense > 0 ? '4px' : '2px' }}
                      ></div>
                      {/* Revenue bar (blue for positive, amber for negative) */}
                      <div
                        className={`w-3 rounded-t transition-all duration-300 ${
                          month.revenue >= 0
                            ? `bg-gradient-to-t from-blue-500 to-blue-400 ${isSelected ? 'from-blue-600 to-blue-500 shadow-lg shadow-blue-500/30' : 'group-hover:from-blue-600 group-hover:to-blue-500'}`
                            : `bg-gradient-to-t from-amber-500 to-amber-400 ${isSelected ? 'from-amber-600 to-amber-500 shadow-lg shadow-amber-500/30' : 'group-hover:from-amber-600 group-hover:to-amber-500'}`
                        }`}
                        style={{ height: `${Math.max((Math.abs(month.revenue) / maxAmount) * 100, 2)}%`, minHeight: month.revenue !== 0 ? '4px' : '2px' }}
                      ></div>
                    </div>
                    {/* Month label */}
                    <span className={`text-[9px] mt-1 font-medium transition-colors truncate w-full text-center ${isSelected ? 'text-teal-600 font-bold' : 'text-stone-500 group-hover:text-teal-600'}`}>
                      {month.monthLabel}
                    </span>
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-teal-500"></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Selected Month Details Card */}
            {selectedMonth && (
              <div className="mt-3 p-3 bg-stone-50 rounded-xl border border-stone-200 animate-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm text-teal-800">{selectedMonth.month}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedMonth(null); }}
                    className="text-stone-400 hover:text-stone-600 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <span className="w-2 h-2 bg-emerald-500 rounded"></span>
                      <span className="text-[10px] text-stone-500 font-medium">Income</span>
                    </div>
                    <p className="text-sm font-bold text-emerald-600">₫{selectedMonth.income.toLocaleString('vi-VN')}</p>
                  </div>
                  <div className="p-2 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <span className="w-2 h-2 bg-red-500 rounded"></span>
                      <span className="text-[10px] text-stone-500 font-medium">Expense</span>
                    </div>
                    <p className="text-sm font-bold text-red-600">₫{selectedMonth.expense.toLocaleString('vi-VN')}</p>
                  </div>
                  <div className="p-2 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <span className={`w-2 h-2 rounded ${selectedMonth.revenue >= 0 ? 'bg-teal-500' : 'bg-amber-500'}`}></span>
                      <span className="text-[10px] text-stone-500 font-medium">Revenue</span>
                    </div>
                    <p className={`text-sm font-bold ${selectedMonth.revenue >= 0 ? 'text-teal-600' : 'text-amber-600'}`}>
                      {selectedMonth.revenue >= 0 ? '+' : ''}₫{selectedMonth.revenue.toLocaleString('vi-VN')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 text-[10px] pt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded"></div>
          <span className="text-stone-600 font-medium">Income</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gradient-to-t from-red-500 to-red-400 rounded"></div>
          <span className="text-stone-600 font-medium">Expense</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gradient-to-t from-blue-500 to-blue-400 rounded"></div>
          <span className="text-stone-600 font-medium">Revenue</span>
        </div>
      </div>

      {/* Period Info */}
      {analytics?.time_range && (
        <div className="text-center text-[10px] text-stone-400">
          {analytics.time_range.start_month} to {analytics.time_range.end_month}
        </div>
      )}
    </div>
  );
};

// Recent Tickets Widget with Luxury List Styling
export const RecentTicketsWidget = ({ timeRange }) => {
  const { data: tickets, isLoading } = useQuery({
    queryKey: ['dashboard-recent-tickets', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: async () => {
      const data = await ticketService.getTickets({
        limit: 5,
        start_date: timeRange?.startDate,
        end_date: timeRange?.endDate
      });
      return Array.isArray(data) ? data.slice(0, 5) : [];
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse flex gap-3 p-2">
            <div className="w-10 h-10 bg-stone-200 rounded-xl"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-stone-200 rounded w-3/4"></div>
              <div className="h-3 bg-stone-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!tickets || !Array.isArray(tickets) || tickets.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-stone-50 border border-stone-100 flex items-center justify-center mb-3">
          <FileText className="w-8 h-8 text-stone-300" />
        </div>
        <p className="text-sm font-medium text-stone-600">No recent tickets</p>
        <p className="text-xs text-stone-400 mt-1">All maintenance requests cleared.</p>
      </div>
    );
  }

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'high': return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' };
      case 'medium': return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' };
      case 'low': return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' };
      default: return { bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-200', dot: 'bg-stone-500' };
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'resolved': return { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' };
      case 'in_progress': return { icon: Clock, color: 'text-teal-500', bg: 'bg-teal-50' };
      default: return { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-50' };
    }
  };

  return (
    <div className="space-y-3 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
      {tickets.map((ticket, index) => {
        const priorityStyle = getPriorityStyle(ticket.priority);
        const statusStyle = getStatusStyle(ticket.status);
        const StatusIcon = statusStyle.icon;

        return (
          <div
            key={ticket.id}
            className="p-3 rounded-xl border border-stone-100 bg-white hover:border-teal-100 hover:shadow-md hover:shadow-teal-100/20 transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl ${statusStyle.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                <StatusIcon className={`w-5 h-5 ${statusStyle.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-stone-800 truncate group-hover:text-teal-600 transition-colors">
                    {ticket.subject}
                  </p>
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${priorityStyle.bg} border ${priorityStyle.border}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${priorityStyle.dot}`}></div>
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${priorityStyle.text}`}>
                      {ticket.priority}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                  <span className="font-medium bg-stone-50 px-1.5 py-0.5 rounded text-stone-600">Room {ticket.room_number || 'N/A'}</span>
                  <span className="w-1 h-1 rounded-full bg-stone-300"></span>
                  <span className="capitalize">{ticket.category}</span>
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Maintenance Alerts Widget with real data
export const MaintenanceAlertsWidget = ({ timeRange }) => {
  const { data: tickets } = useQuery({
    queryKey: ['dashboard-maintenance-alerts', timeRange?.startMonth],
    queryFn: async () => {
      const data = await ticketService.getTickets({
        category: 'maintenance',
        status: 'open,in_progress'
      });
      return data;
    }
  });

  const alertCount = Array.isArray(tickets) ? tickets.length : 0;
  const highPriorityCount = Array.isArray(tickets) ? tickets.filter(t => t.priority === 'high').length : 0;

  return (
    <div>
      <div className={`flex items-center gap-2 p-3 border rounded ${highPriorityCount > 0
          ? 'bg-red-50 border-red-200'
          : alertCount > 0
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-green-50 border-green-200'
        }`}>
        <AlertTriangle className={`w-5 h-5 ${highPriorityCount > 0
            ? 'text-red-600'
            : alertCount > 0
              ? 'text-yellow-600'
              : 'text-green-600'
          }`} />
        <div>
          <p className="text-sm font-medium">
            {alertCount} Pending {alertCount === 1 ? 'Alert' : 'Alerts'}
          </p>
          <p className="text-xs text-stone-600">
            {highPriorityCount > 0
              ? `${highPriorityCount} high priority`
              : alertCount > 0
                ? 'Requires attention'
                : 'All clear'}
          </p>
        </div>
      </div>
      {alertCount > 0 && (
        <div className="mt-2 space-y-1">
          {tickets.slice(0, 3).map(ticket => (
            <div key={ticket.id} className="text-xs p-1 bg-white rounded border">
              Room {ticket.room_number}: {ticket.subject}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Tenant Statistics Widget - Luxury Cards
export const TenantStatsWidget = ({ timeRange }) => {
  const { data: renters } = useQuery({
    queryKey: ['dashboard-renters', timeRange?.startMonth],
    queryFn: () => userService.getRenters()
  });

  const { data: contracts } = useQuery({
    queryKey: ['dashboard-contracts', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => contractService.getContracts()
  });

  const stats = React.useMemo(() => {
    // Use timeRange if available, otherwise use current date
    const startDate = timeRange?.startDate ? new Date(timeRange.startDate) : new Date();
    const endDate = timeRange?.endDate ? new Date(timeRange.endDate) : new Date();

    const activeRenters = Array.isArray(renters) ? renters.filter(r => r.is_active) : [];

    // Count contracts created within the time range
    const newInPeriod = Array.isArray(contracts) ? contracts.filter(c => {
      const contractStart = new Date(c.start_date);
      return contractStart >= startDate && contractStart <= endDate;
    }).length : 0;

    // Count contracts expiring within 30 days from endDate
    const expiringCount = Array.isArray(contracts) ? contracts.filter(c => {
      const contractEnd = new Date(c.end_date);
      const daysUntilExpiry = (contractEnd - endDate) / (1000 * 60 * 60 * 24);
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
    }).length : 0;

    return {
      total: activeRenters.length,
      newInPeriod,
      expiringSoon: expiringCount
    };
  }, [renters, contracts, timeRange]);

  const statItems = [
    {
      value: stats.total,
      label: 'Total Tenants',
      icon: Users,
      gradient: 'from-teal-500 to-teal-600',
      bgLight: 'bg-teal-50',
      textColor: 'text-teal-900',
      borderColor: 'border-teal-100'
    },
    {
      value: stats.newInPeriod,
      label: 'New in Period',
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-teal-600',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-900',
      borderColor: 'border-emerald-100'
    },
    {
      value: stats.expiringSoon,
      label: 'Expiring Soon',
      icon: AlertTriangle,
      gradient: 'from-amber-500 to-orange-600',
      bgLight: 'bg-amber-50',
      textColor: 'text-amber-900',
      borderColor: 'border-amber-100'
    }
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {statItems.map((item, index) => (
        <div
          key={index}
          className={`flex flex-col items-center justify-center p-3 rounded-2xl ${item.bgLight} border ${item.borderColor} shadow-sm hover:shadow-md transition-all duration-300 group`}
        >
          <div className={`w-10 h-10 mb-2 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <item.icon className="w-5 h-5 text-white" />
          </div>
          <p className={`text-2xl font-bold ${item.textColor} tracking-tight`}>{item.value}</p>
          <p className="text-[10px] text-stone-500 font-bold uppercase tracking-wide mt-1 text-center leading-tight">{item.label}</p>
        </div>
      ))}
    </div>
  );
};

// Payment Trends Widget with real data
export const PaymentTrendsWidget = ({ timeRange }) => {
  const { data: payments } = useQuery({
    queryKey: ['dashboard-payment-trends', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getPayments({
      start_month: timeRange?.startMonth,
      end_month: timeRange?.endMonth
    })
  });

  const trends = React.useMemo(() => {
    if (!payments || !Array.isArray(payments)) return { onTime: 0, late: 0, pending: 0, total: 0 };

    // Filter payments within the time range
    const startDate = timeRange?.startDate ? new Date(timeRange.startDate) : null;
    const endDate = timeRange?.endDate ? new Date(timeRange.endDate) : null;

    const filteredPayments = payments.filter(p => {
      if (!startDate || !endDate) return true;
      const payDate = new Date(p.payment_date || p.created_at);
      return payDate >= startDate && payDate <= endDate;
    });

    return {
      onTime: filteredPayments.filter(p => (p.status === 'confirmed' || p.status === 'paid') && !p.is_late).length,
      late: filteredPayments.filter(p => (p.status === 'confirmed' || p.status === 'paid') && p.is_late).length,
      pending: filteredPayments.filter(p => p.status === 'pending').length,
      total: filteredPayments.reduce((sum, p) =>
        (p.status === 'confirmed' || p.status === 'paid') ? sum + parseFloat(p.amount || p.paid_amount || 0) : sum, 0
      )
    };
  }, [payments, timeRange]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-stone-700">Payment Status</h4>
        <TrendingUp className="w-4 h-4 text-green-600" />
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-stone-600">On Time</span>
          <span className="text-sm font-medium text-green-600">{trends.onTime}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-stone-600">Late</span>
          <span className="text-sm font-medium text-red-600">{trends.late}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-stone-600">Pending</span>
          <span className="text-sm font-medium text-yellow-600">{trends.pending}</span>
        </div>
        <div className="pt-2 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total Collected</span>
            <span className="text-sm font-bold text-teal-600">
              ₫{trends.total.toLocaleString('vi-VN')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Building Overview Widget with real data
export const BuildingOverviewWidget = ({ timeRange }) => {
  const { data: buildings, isLoading } = useQuery({
    queryKey: ['dashboard-buildings', timeRange?.startMonth],
    queryFn: () => propertyService.getBuildings()
  });

  const { data: rooms } = useQuery({
    queryKey: ['dashboard-building-rooms', timeRange?.startMonth],
    queryFn: () => propertyService.getRooms()
  });

  const buildingStats = React.useMemo(() => {
    if (!buildings || !rooms || !Array.isArray(buildings) || !Array.isArray(rooms)) return [];

    return buildings.map(building => {
      const buildingRooms = rooms.filter(r => r.building_id === building.id);
      const occupiedRooms = buildingRooms.filter(r => r.status === 'occupied').length;
      const totalRooms = buildingRooms.length;
      const occupancyRate = totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0;

      return {
        ...building,
        occupiedRooms,
        totalRooms,
        occupancyRate
      };
    });
  }, [buildings, rooms]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-10 bg-stone-100 rounded"></div>
        ))}
      </div>
    );
  }

  if (!buildingStats.length) {
    return (
      <div className="text-center py-4">
        <Building2 className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <p className="text-sm text-stone-500">No buildings found</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {buildingStats.slice(0, 5).map((building) => (
        <div key={building.id} className="flex justify-between items-center p-2 bg-stone-50 rounded hover:bg-stone-100 transition-colors">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium">{building.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-600">
              {building.occupiedRooms}/{building.totalRooms}
            </span>
            <span className={`text-sm font-medium ${building.occupancyRate >= 90 ? 'text-green-600' :
                building.occupancyRate >= 70 ? 'text-yellow-600' :
                  'text-red-600'
              }`}>
              {building.occupancyRate}%
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Activity Feed Widget with real data
export const ActivityFeedWidget = ({ timeRange }) => {
  const [activities, setActivities] = useState([]);

  const { data: recentContracts } = useQuery({
    queryKey: ['dashboard-recent-contracts', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: async () => {
      const contracts = await contractService.getContracts({
        limit: 5,
        start_date: timeRange?.startDate,
        end_date: timeRange?.endDate
      });
      return Array.isArray(contracts) ? contracts.slice(0, 5) : [];
    }
  });

  const { data: recentPayments } = useQuery({
    queryKey: ['dashboard-recent-payments', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: async () => {
      const payments = await financeService.getPayments({
        limit: 5,
        start_month: timeRange?.startMonth,
        end_month: timeRange?.endMonth
      });
      return Array.isArray(payments) ? payments.slice(0, 5) : [];
    }
  });

  const { data: recentTickets } = useQuery({
    queryKey: ['dashboard-recent-activity-tickets', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: async () => {
      const tickets = await ticketService.getTickets({
        limit: 5,
        start_date: timeRange?.startDate,
        end_date: timeRange?.endDate
      });
      return Array.isArray(tickets) ? tickets.slice(0, 5) : [];
    }
  });

  useEffect(() => {
    const allActivities = [];

    // Add contracts
    if (Array.isArray(recentContracts)) {
      recentContracts.forEach(contract => {
        allActivities.push({
          id: `contract-${contract.id}`,
          type: 'contract',
          message: `Contract #${contract.contract_number} created`,
          time: new Date(contract.created_at),
          icon: FileText
        });
      });
    }

    // Add payments
    if (Array.isArray(recentPayments)) {
      recentPayments.forEach(payment => {
        allActivities.push({
          id: `payment-${payment.id}`,
          type: 'payment',
          message: `Payment of ₫${parseFloat(payment.amount).toLocaleString('vi-VN')} received`,
          time: new Date(payment.payment_date),
          icon: DollarSign
        });
      });
    }

    // Add tickets
    if (Array.isArray(recentTickets)) {
      recentTickets.forEach(ticket => {
        allActivities.push({
          id: `ticket-${ticket.id}`,
          type: 'ticket',
          message: `Ticket #${ticket.id}: ${ticket.subject}`,
          time: new Date(ticket.created_at),
          icon: AlertCircle
        });
      });
    }

    // Sort by time, most recent first
    allActivities.sort((a, b) => b.time - a.time);
    setActivities(allActivities.slice(0, 5));
  }, [recentContracts, recentPayments, recentTickets]);

  const getTypeColor = (type) => {
    switch (type) {
      case 'contract': return 'text-teal-600 bg-teal-50';
      case 'payment': return 'text-green-600 bg-green-50';
      case 'ticket': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-stone-600 bg-stone-50';
    }
  };

  const formatTime = (time) => {
    const now = new Date();
    const diff = now - time;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (!activities.length) {
    return (
      <div className="text-center py-4">
        <Activity className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <p className="text-sm text-stone-500">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activities.map((activity) => {
        const Icon = activity.icon;
        return (
          <div key={activity.id} className="flex items-start gap-2 p-2 rounded hover:bg-stone-50 transition-colors">
            <div className={`p-1 rounded ${getTypeColor(activity.type)}`}>
              <Icon className="w-3 h-3" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-800 truncate">{activity.message}</p>
              <p className="text-xs text-stone-500">{formatTime(activity.time)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Room Income Widget - Shows income breakdown per room
export const RoomIncomeWidget = ({ timeRange }) => {
  const [selectedBuilding, setSelectedBuilding] = useState('all');

  // Use endMonth from timeRange as the selected month (most recent month in range)
  const selectedMonth = timeRange?.endMonth || new Date().toISOString().slice(0, 7);

  const { data: buildings } = useQuery({
    queryKey: ['dashboard-income-buildings', timeRange?.startMonth],
    queryFn: () => propertyService.getBuildings()
  });

  const { data: rooms } = useQuery({
    queryKey: ['dashboard-income-rooms', timeRange?.startMonth],
    queryFn: () => propertyService.getRooms()
  });

  const { data: payments } = useQuery({
    queryKey: ['dashboard-income-payments', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getPayments({
      start_month: timeRange?.startMonth,
      end_month: timeRange?.endMonth
    })
  });

  const { data: meterReadings } = useQuery({
    queryKey: ['dashboard-income-meters', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getMeterReadings()
  });

  const roomIncomeData = React.useMemo(() => {
    if (!rooms || !payments || !Array.isArray(rooms) || !Array.isArray(payments)) return [];

    const filteredRooms = selectedBuilding === 'all'
      ? rooms
      : rooms.filter(r => r.building_id === parseInt(selectedBuilding));

    const startMonth = timeRange?.startMonth;
    const endMonth = timeRange?.endMonth;

    return filteredRooms.map(room => {
      // Get payments for this room within time range
      const roomPayments = payments.filter(p => {
        if (p.room_id !== room.id) return false;
        if (p.status !== 'confirmed' && p.status !== 'paid') return false;
        if (startMonth && endMonth && p.month) {
          return p.month >= startMonth && p.month <= endMonth;
        }
        return true;
      });

      const rentIncome = roomPayments.reduce((sum, p) =>
        sum + (parseFloat(p.paid_amount) || 0), 0
      );

      // Get meter costs for this room within time range
      const roomMeters = meterReadings?.filter(m => {
        if (m.room_id !== room.id) return false;
        if (startMonth && endMonth && m.month) {
          return m.month >= startMonth && m.month <= endMonth;
        }
        return true;
      }) || [];

      const electricityCost = roomMeters
        .filter(m => m.meter_type?.toLowerCase().includes('electric'))
        .reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);

      const waterCost = roomMeters
        .filter(m => m.meter_type?.toLowerCase().includes('water'))
        .reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);

      return {
        id: room.id,
        roomNumber: room.room_number,
        floor: room.floor,
        buildingName: buildings?.find(b => b.id === room.building_id)?.name || 'Unknown',
        status: room.status,
        rentIncome,
        electricityCost,
        waterCost,
        totalIncome: rentIncome + electricityCost + waterCost
      };
    }).sort((a, b) => b.totalIncome - a.totalIncome);
  }, [rooms, payments, meterReadings, buildings, selectedBuilding, timeRange]);

  const totalIncome = roomIncomeData.reduce((sum, r) => sum + r.totalIncome, 0);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <select
          value={selectedBuilding}
          onChange={(e) => setSelectedBuilding(e.target.value)}
          className="flex-1 text-sm border rounded-lg px-2 py-1.5 bg-stone-50"
        >
          <option value="all">All Buildings</option>
          {Array.isArray(buildings) && buildings.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-lg">
        <p className="text-xs opacity-80">Total Income ({timeRange?.startMonth} - {timeRange?.endMonth})</p>
        <p className="text-xl font-bold">₫{totalIncome.toLocaleString('vi-VN')}</p>
      </div>

      <div className="max-h-60 overflow-y-auto space-y-2">
        {roomIncomeData.slice(0, 10).map((room) => (
          <div
            key={room.id}
            className={`p-2 rounded border ${room.status === 'occupied' ? 'bg-green-50 border-green-200' : 'bg-stone-50 border-stone-200'
              }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-sm">Room {room.roomNumber}</p>
                <p className="text-xs text-stone-500">{room.buildingName} • Floor {room.floor}</p>
              </div>
              <span className={`text-sm font-bold ${room.totalIncome > 0 ? 'text-green-600' : 'text-stone-400'}`}>
                ₫{room.totalIncome.toLocaleString('vi-VN')}
              </span>
            </div>
            {room.totalIncome > 0 && (
              <div className="mt-1 flex gap-3 text-xs text-stone-500">
                <span>Rent: ₫{room.rentIncome.toLocaleString('vi-VN')}</span>
                <span>Elec: ₫{room.electricityCost.toLocaleString('vi-VN')}</span>
                <span>Water: ₫{room.waterCost.toLocaleString('vi-VN')}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Building Financial Summary Widget
export const BuildingFinancialWidget = ({ timeRange }) => {
  const { data: buildings } = useQuery({
    queryKey: ['dashboard-financial-buildings', timeRange?.startMonth],
    queryFn: () => propertyService.getBuildings()
  });

  const { data: rooms } = useQuery({
    queryKey: ['dashboard-financial-rooms', timeRange?.startMonth],
    queryFn: () => propertyService.getRooms()
  });

  const { data: payments } = useQuery({
    queryKey: ['dashboard-financial-payments', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getPayments({
      start_month: timeRange?.startMonth,
      end_month: timeRange?.endMonth
    })
  });

  const { data: meterReadings } = useQuery({
    queryKey: ['dashboard-financial-meters', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getMeterReadings({
      start_month: timeRange?.startMonth,
      end_month: timeRange?.endMonth
    })
  });

  const buildingFinancials = React.useMemo(() => {
    if (!buildings || !rooms || !payments || !Array.isArray(buildings) || !Array.isArray(rooms) || !Array.isArray(payments)) return [];

    const startMonth = timeRange?.startMonth;
    const endMonth = timeRange?.endMonth;

    return buildings.map(building => {
      const buildingRooms = rooms.filter(r => r.building_id === building.id);
      const roomIds = buildingRooms.map(r => r.id);

      // Calculate income within time range
      const buildingPayments = payments.filter(p => {
        if (!roomIds.includes(p.room_id)) return false;
        if (startMonth && endMonth && p.month) {
          return p.month >= startMonth && p.month <= endMonth;
        }
        return true;
      });

      const confirmedPayments = buildingPayments.filter(p => p.status === 'confirmed' || p.status === 'paid');
      const pendingPayments = buildingPayments.filter(p => p.status === 'pending');

      const confirmedIncome = confirmedPayments.reduce((sum, p) =>
        sum + (parseFloat(p.paid_amount) || 0), 0
      );
      const pendingIncome = pendingPayments.reduce((sum, p) =>
        sum + (parseFloat(p.final_amount ?? p.invoice_amount) || 0), 0
      );

      // Calculate meter revenue within time range
      const buildingMeters = meterReadings?.filter(m => {
        if (!roomIds.includes(m.room_id)) return false;
        if (startMonth && endMonth && m.month) {
          return m.month >= startMonth && m.month <= endMonth;
        }
        return true;
      }) || [];

      const meterRevenue = buildingMeters.reduce((sum, m) =>
        sum + (parseFloat(m.cost) || 0), 0
      );

      // Occupancy
      const occupiedRooms = buildingRooms.filter(r => r.status === 'occupied').length;
      const occupancyRate = buildingRooms.length > 0
        ? Math.round((occupiedRooms / buildingRooms.length) * 100)
        : 0;

      return {
        id: building.id,
        name: building.name,
        totalRooms: buildingRooms.length,
        occupiedRooms,
        occupancyRate,
        confirmedIncome,
        pendingIncome,
        meterRevenue,
        totalRevenue: confirmedIncome + meterRevenue
      };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [buildings, rooms, payments, meterReadings, timeRange]);

  const grandTotal = buildingFinancials.reduce((sum, b) => sum + b.totalRevenue, 0);
  const totalPending = buildingFinancials.reduce((sum, b) => sum + b.pendingIncome, 0);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-stone-700">Building Revenue</h4>
        <span className="text-xs text-stone-500">{timeRange?.startMonth} - {timeRange?.endMonth}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
          <p className="text-xs text-green-600">Collected</p>
          <p className="text-lg font-bold text-green-700">₫{grandTotal.toLocaleString('vi-VN')}</p>
        </div>
        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
          <p className="text-xs text-yellow-600">Pending</p>
          <p className="text-lg font-bold text-yellow-700">₫{totalPending.toLocaleString('vi-VN')}</p>
        </div>
      </div>

      <div className="max-h-48 overflow-y-auto space-y-2">
        {buildingFinancials.map((building) => (
          <div
            key={building.id}
            className="p-3 bg-stone-50 rounded-lg border hover:bg-stone-100 transition-colors"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-stone-500" />
                <span className="font-medium">{building.name}</span>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded ${building.occupancyRate >= 90 ? 'bg-green-100 text-green-700' :
                  building.occupancyRate >= 70 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                }`}>
                {building.occupancyRate}% occupied
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-stone-500">Rooms</p>
                <p className="font-medium">{building.occupiedRooms}/{building.totalRooms}</p>
              </div>
              <div>
                <p className="text-stone-500">Rent</p>
                <p className="font-medium text-green-600">₫{building.confirmedIncome.toLocaleString('vi-VN')}</p>
              </div>
              <div>
                <p className="text-stone-500">Utilities</p>
                <p className="font-medium text-teal-600">₫{building.meterRevenue.toLocaleString('vi-VN')}</p>
              </div>
            </div>

            <div className="mt-2 pt-2 border-t flex justify-between items-center">
              <span className="text-xs text-stone-600">Total Revenue</span>
              <span className="font-bold text-green-600">
                ₫{building.totalRevenue.toLocaleString('vi-VN')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// METER USAGE ANALYTICS WIDGETS
// ============================================

// Electric Usage Widget - Time Series + Room Breakdown
/**
 * Monthly usage trend — SVG line chart with always-visible per-point labels.
 *
 * Props:
 *   data:  Array<{ month: string ("YYYY-MM"), total_usage: number, total_cost: number }>
 *   unit:  string — display unit suffix on usage values (e.g. "kWh" or "m³")
 *   color: { line, dot, area } — tailwind/hex color values for the line, dots, and gradient fill
 */
const MonthlyTrendChart = ({ data, unit, color }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center bg-stone-50 rounded-xl">
        <p className="text-sm text-stone-400">No data available</p>
      </div>
    );
  }

  // Drawing area (viewBox units; CSS scales it to 100% width).
  const W = 480;
  const H = 140;
  const PAD_X = 28;
  const PAD_TOP = 22;     // room for usage value above the dot
  const PAD_BOTTOM = 36;  // room for month + cost below the dot
  const plotW = W - PAD_X * 2;
  const plotH = H - PAD_TOP - PAD_BOTTOM;

  const maxUsage = Math.max(...data.map(d => d.total_usage), 1);
  const maxCost = Math.max(...data.map(d => d.total_cost), 1);
  const xAt = (i) => data.length === 1
    ? W / 2
    : PAD_X + (i / (data.length - 1)) * plotW;
  const yAt = (v) => PAD_TOP + plotH - (v / maxUsage) * plotH;
  // Cost bars sit on a separate scale (different magnitude than usage).
  // Cap at 85% of plotH so the line stays readable above tall bars.
  const baseY = PAD_TOP + plotH;
  const barH = (v) => Math.max((v / maxCost) * (plotH * 0.85), v > 0 ? 2 : 0);
  // Bar width: a fraction of the slot between adjacent points, clamped.
  const slot = data.length > 1 ? plotW / (data.length - 1) : plotW;
  const BAR_W = Math.max(14, Math.min(slot * 0.45, 40));

  const points = data.map((d, i) => ({ x: xAt(i), y: yAt(d.total_usage), ...d }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${(PAD_TOP + plotH).toFixed(1)} L ${points[0].x.toFixed(1)} ${(PAD_TOP + plotH).toFixed(1)} Z`;

  const gradId = `trend-grad-${unit.replace(/[^a-z]/gi, '')}`;
  const fmtCost = (n) => {
    if (n >= 1_000_000) return `₫${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1_000) return `₫${Math.round(n / 1_000)}k`;
    return `₫${n}`;
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" role="img" aria-label="Monthly usage trend">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color.area} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color.area} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Cost columns — render behind the line. Subtle fill so the line stays primary. */}
      {points.map((p, i) => {
        const h = barH(p.total_cost);
        return (
          <rect
            key={`bar-${i}`}
            x={p.x - BAR_W / 2}
            y={baseY - h}
            width={BAR_W}
            height={h}
            rx="2"
            fill={color.area}
            fillOpacity="0.18"
          />
        );
      })}

      {/* Area under line */}
      <path d={areaPath} fill={`url(#${gradId})`} />
      {/* Line */}
      <path d={linePath} fill="none" stroke={color.line} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

      {/* Dots + labels */}
      {points.map((p, i) => {
        const monthName = new Date(p.month + '-01').toLocaleString('default', { month: 'short' });
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#ffffff" stroke={color.dot} strokeWidth="2" />
            {/* Usage value — above the dot */}
            <text
              x={p.x} y={p.y - 9}
              textAnchor="middle"
              className="num"
              style={{ fontSize: '10px', fontWeight: 600, fill: '#1c1917' }}
            >
              {p.total_usage.toLocaleString('vi-VN')} {unit}
            </text>
            {/* Month label */}
            <text
              x={p.x} y={H - PAD_BOTTOM + 16}
              textAnchor="middle"
              style={{ fontSize: '10px', fill: '#78716c' }}
            >
              {monthName}
            </text>
            {/* Cost label — below month */}
            <text
              x={p.x} y={H - PAD_BOTTOM + 28}
              textAnchor="middle"
              className="num"
              style={{ fontSize: '9px', fill: '#a8a29e' }}
            >
              {fmtCost(p.total_cost)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export const ElectricUsageWidget = ({ timeRange }) => {
  const [selectedBuilding, setSelectedBuilding] = React.useState(null);
  const [showAllRooms, setShowAllRooms] = React.useState(false);

  const { data: buildings } = useQuery({
    queryKey: ['buildings-list'],
    queryFn: () => propertyService.getBuildings()
  });

  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['electric-usage', timeRange?.startMonth, timeRange?.endMonth, selectedBuilding],
    queryFn: () => financeService.getMeterUsageByRooms('electricity', {
      startMonth: timeRange?.startMonth,
      endMonth: timeRange?.endMonth,
      buildingId: selectedBuilding
    })
  });

  const maxUsage = React.useMemo(() => {
    if (!analytics?.monthly_trend) return 1;
    return Math.max(...analytics.monthly_trend.map(m => m.total_usage), 1);
  }, [analytics]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-16 bg-stone-100 rounded-xl"></div>
        <div className="h-32 bg-stone-50 rounded-xl"></div>
        <div className="h-24 bg-stone-100 rounded-xl"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading electric usage</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  const summary = analytics?.summary || { total_usage: 0, total_cost: 0, avg_per_room: 0, room_count: 0 };
  const monthlyTrend = analytics?.monthly_trend || [];
  const rooms = analytics?.rooms || [];
  const displayRooms = showAllRooms ? rooms : rooms.slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Header with Building Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-100 rounded-lg">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <span className="font-semibold text-stone-700">Electric Usage</span>
        </div>
        <select
          value={selectedBuilding || ''}
          onChange={(e) => setSelectedBuilding(e.target.value ? parseInt(e.target.value) : null)}
          className="text-xs border border-stone-200 rounded-lg px-2 py-1 bg-white"
        >
          <option value="">All Buildings</option>
          {buildings?.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-100">
          <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-1">Total Usage</p>
          <p className="text-lg font-bold text-amber-700">{summary.total_usage.toLocaleString('vi-VN')} kWh</p>
        </div>
        <div className="p-3 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border border-orange-100">
          <p className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-1">Total Cost</p>
          <p className="text-lg font-bold text-orange-700">₫{summary.total_cost.toLocaleString('vi-VN')}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex justify-between text-xs text-stone-500 px-1">
        <span>Avg/Room: <strong className="text-stone-700">{summary.avg_per_room.toLocaleString()} kWh</strong></span>
        <span>Rooms: <strong className="text-stone-700">{summary.room_count}</strong></span>
      </div>

      {/* Monthly Trend Chart */}
      <div className="relative">
        <p className="text-xs font-medium text-stone-500 mb-2">Monthly Trend</p>
        <MonthlyTrendChart
          data={monthlyTrend}
          unit="kWh"
          color={{ line: '#d97706', dot: '#d97706', area: '#f59e0b' }}
        />
      </div>

      {/* Room Usage Breakdown */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-xs font-medium text-stone-500">Room Usage (Top Consumers)</p>
          {rooms.length > 5 && (
            <button
              onClick={() => setShowAllRooms(!showAllRooms)}
              className="text-xs text-amber-600 hover:text-amber-700 font-medium"
            >
              {showAllRooms ? 'Show Less' : `See All (${rooms.length})`}
            </button>
          )}
        </div>
        <div className={`space-y-1.5 ${showAllRooms ? 'max-h-48 overflow-y-auto' : ''}`}>
          {displayRooms.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-2">No room data</p>
          ) : (
            displayRooms.map((room, index) => {
              const maxRoomUsage = rooms[0]?.total_usage || 1;
              const percentage = (room.total_usage / maxRoomUsage) * 100;
              return (
                <div key={room.room_id} className="flex items-center gap-2 text-xs">
                  <span className="w-16 truncate text-stone-600" title={`${room.room_number} (${room.building_name})`}>
                    {room.room_number}
                  </span>
                  <div className="flex-1 h-4 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-20 text-right font-medium text-stone-700">
                    {room.total_usage.toLocaleString()} kWh
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// Water Usage Widget - Time Series + Room Breakdown
export const WaterUsageWidget = ({ timeRange }) => {
  const [selectedBuilding, setSelectedBuilding] = React.useState(null);
  const [showAllRooms, setShowAllRooms] = React.useState(false);

  const { data: buildings } = useQuery({
    queryKey: ['buildings-list'],
    queryFn: () => propertyService.getBuildings()
  });

  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['water-usage', timeRange?.startMonth, timeRange?.endMonth, selectedBuilding],
    queryFn: () => financeService.getMeterUsageByRooms('water', {
      startMonth: timeRange?.startMonth,
      endMonth: timeRange?.endMonth,
      buildingId: selectedBuilding
    })
  });

  const maxUsage = React.useMemo(() => {
    if (!analytics?.monthly_trend) return 1;
    return Math.max(...analytics.monthly_trend.map(m => m.total_usage), 1);
  }, [analytics]);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-16 bg-stone-100 rounded-xl"></div>
        <div className="h-32 bg-stone-50 rounded-xl"></div>
        <div className="h-24 bg-stone-100 rounded-xl"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading water usage</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  const summary = analytics?.summary || { total_usage: 0, total_cost: 0, avg_per_room: 0, room_count: 0 };
  const monthlyTrend = analytics?.monthly_trend || [];
  const rooms = analytics?.rooms || [];
  const displayRooms = showAllRooms ? rooms : rooms.slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Header with Building Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-teal-100 rounded-lg">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <span className="font-semibold text-stone-700">Water Usage</span>
        </div>
        <select
          value={selectedBuilding || ''}
          onChange={(e) => setSelectedBuilding(e.target.value ? parseInt(e.target.value) : null)}
          className="text-xs border border-stone-200 rounded-lg px-2 py-1 bg-white"
        >
          <option value="">All Buildings</option>
          {buildings?.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-teal-100">
          <p className="text-[10px] text-teal-600 font-bold uppercase tracking-wider mb-1">Total Usage</p>
          <p className="text-lg font-bold text-teal-700">{summary.total_usage.toLocaleString('vi-VN')} m³</p>
        </div>
        <div className="p-3 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl border border-cyan-100">
          <p className="text-[10px] text-cyan-600 font-bold uppercase tracking-wider mb-1">Total Cost</p>
          <p className="text-lg font-bold text-cyan-700">₫{summary.total_cost.toLocaleString('vi-VN')}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex justify-between text-xs text-stone-500 px-1">
        <span>Avg/Room: <strong className="text-stone-700">{summary.avg_per_room.toLocaleString()} m³</strong></span>
        <span>Rooms: <strong className="text-stone-700">{summary.room_count}</strong></span>
      </div>

      {/* Monthly Trend Chart */}
      <div className="relative">
        <p className="text-xs font-medium text-stone-500 mb-2">Monthly Trend</p>
        <MonthlyTrendChart
          data={monthlyTrend}
          unit="m³"
          color={{ line: '#0284c7', dot: '#0284c7', area: '#0ea5e9' }}
        />
      </div>

      {/* Room Usage Breakdown */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-xs font-medium text-stone-500">Room Usage (Top Consumers)</p>
          {rooms.length > 5 && (
            <button
              onClick={() => setShowAllRooms(!showAllRooms)}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              {showAllRooms ? 'Show Less' : `See All (${rooms.length})`}
            </button>
          )}
        </div>
        <div className={`space-y-1.5 ${showAllRooms ? 'max-h-48 overflow-y-auto' : ''}`}>
          {displayRooms.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-2">No room data</p>
          ) : (
            displayRooms.map((room, index) => {
              const maxRoomUsage = rooms[0]?.total_usage || 1;
              const percentage = (room.total_usage / maxRoomUsage) * 100;
              return (
                <div key={room.room_id} className="flex items-center gap-2 text-xs">
                  <span className="w-16 truncate text-stone-600" title={`${room.room_number} (${room.building_name})`}>
                    {room.room_number}
                  </span>
                  <div className="flex-1 h-4 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-16 text-right font-medium text-stone-700">
                    {room.total_usage.toLocaleString()} m³
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// Building Usage Comparison Widget
export const BuildingUsageComparisonWidget = ({ timeRange }) => {
  const [readingType, setReadingType] = React.useState('electricity');

  const { data: comparison, isLoading, error } = useQuery({
    queryKey: ['building-comparison', timeRange?.endMonth, readingType],
    queryFn: () => financeService.getBuildingMeterComparison(readingType, {
      month: timeRange?.endMonth
    })
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-stone-100 rounded-xl"></div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-stone-50 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading comparison</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  const buildings = comparison?.buildings || [];
  const systemAverage = comparison?.system_average || 0;
  const unit = readingType === 'electricity' ? 'kWh' : 'm³';
  const maxUsage = Math.max(...buildings.map(b => b.avg_per_room), systemAverage, 1);

  return (
    <div className="space-y-4">
      {/* Header with Type Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-stone-500" />
          <span className="font-semibold text-stone-700">Building Comparison</span>
        </div>
        <div className="flex bg-stone-100 rounded-lg p-0.5">
          <button
            onClick={() => setReadingType('electricity')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              readingType === 'electricity'
                ? 'bg-white text-amber-600 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Electric
          </button>
          <button
            onClick={() => setReadingType('water')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              readingType === 'water'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Water
          </button>
        </div>
      </div>

      {/* System Average Indicator */}
      <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
        <div className="flex justify-between items-center">
          <span className="text-xs text-stone-500">System Average</span>
          <span className="font-bold text-stone-700">{systemAverage.toLocaleString()} {unit}/room</span>
        </div>
      </div>

      {/* Building Comparison Bars */}
      <div className="space-y-3">
        {buildings.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-4">No data for this month</p>
        ) : (
          buildings.map((building) => {
            const isAbove = building.variance_pct > 5;
            const isBelow = building.variance_pct < -5;
            const barColor = readingType === 'electricity'
              ? (isAbove ? 'from-red-400 to-red-500' : isBelow ? 'from-green-400 to-green-500' : 'from-amber-400 to-amber-500')
              : (isAbove ? 'from-red-400 to-red-500' : isBelow ? 'from-green-400 to-green-500' : 'from-blue-400 to-blue-500');

            return (
              <div key={building.building_id} className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium text-stone-700 truncate max-w-32" title={building.name}>
                    {building.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      isAbove ? 'bg-red-100 text-red-600' :
                      isBelow ? 'bg-green-100 text-green-600' :
                      'bg-stone-100 text-stone-600'
                    }`}>
                      {building.variance_pct > 0 ? '+' : ''}{building.variance_pct}%
                    </span>
                    <span className="font-medium text-stone-700">{building.avg_per_room.toLocaleString()} {unit}</span>
                  </div>
                </div>
                <div className="relative h-5 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${barColor} rounded-full transition-all`}
                    style={{ width: `${(building.avg_per_room / maxUsage) * 100}%` }}
                  />
                  {/* Average line indicator */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-stone-400"
                    style={{ left: `${(systemAverage / maxUsage) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-stone-400">
                  <span>{building.room_count} rooms</span>
                  <span>₫{building.total_cost.toLocaleString()}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 text-[10px] text-stone-500 pt-2 border-t">
        <span className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500"></div> Below Avg
        </span>
        <span className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-stone-400"></div> Average
        </span>
        <span className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500"></div> Above Avg
        </span>
      </div>
    </div>
  );
};

// Meter Prediction Widget
export const MeterPredictionWidget = ({ timeRange }) => {
  const { data: prediction, isLoading, error } = useQuery({
    queryKey: ['meter-prediction'],
    queryFn: () => financeService.getMeterPrediction({ monthsAhead: 3 }),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-12 bg-stone-100 rounded-xl"></div>
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-stone-50 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading predictions</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  const predictions = prediction?.predictions || [];
  const historicalAvg = prediction?.historical_average || { electricity: { cost: 0 }, water: { cost: 0 } };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="p-2 bg-purple-100 rounded-lg">
          <TrendingUp className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <span className="font-semibold text-stone-700">Cost Prediction</span>
          <p className="text-[10px] text-stone-400">Based on 6-month trend</p>
        </div>
      </div>

      {/* Historical Average */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-[9px] text-amber-600 font-medium uppercase">Avg Electric/mo</p>
          <p className="text-sm font-bold text-amber-700">₫{(historicalAvg.electricity?.cost || 0).toLocaleString()}</p>
        </div>
        <div className="p-2 bg-teal-50 rounded-lg border border-teal-100">
          <p className="text-[9px] text-teal-600 font-medium uppercase">Avg Water/mo</p>
          <p className="text-sm font-bold text-teal-700">₫{(historicalAvg.water?.cost || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Prediction Cards */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-stone-500">Forecast (Next 3 Months)</p>
        {predictions.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-4">Insufficient data for prediction</p>
        ) : (
          <div className="space-y-2">
            {predictions.map((pred, index) => (
              <div key={pred.month} className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-stone-700">
                      {new Date(pred.month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-[10px] text-stone-400">Month +{index + 1}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-700">
                      ₫{pred.total_predicted_cost.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-1 justify-end">
                      <div className="w-12 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full"
                          style={{ width: `${pred.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-stone-500">{Math.round(pred.confidence * 100)}%</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    <span className="text-stone-500">Electric:</span>
                    <span className="font-medium">₫{(pred.electricity?.predicted_cost || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                    <span className="text-stone-500">Water:</span>
                    <span className="font-medium">₫{(pred.water?.predicted_cost || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-stone-400 text-center italic">
        Predictions based on weighted moving average of historical data
      </p>
    </div>
  );
};

// Service Fee Adjustments/Credits Widget
export const AdjustmentsWidget = ({ timeRange }) => {
  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['adjustments-analytics', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getAdjustmentsAnalytics({
      startMonth: timeRange?.startMonth,
      endMonth: timeRange?.endMonth
    }),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-20 bg-stone-100 rounded-xl"></div>
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-stone-50 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
        <p className="font-semibold">Error loading adjustments</p>
        <p className="text-xs mt-1">{error.message}</p>
      </div>
    );
  }

  const summary = analytics?.summary || { total_adjusted: 0, total_count: 0, by_type: {} };
  const byService = analytics?.by_service || [];
  const recentAdjustments = analytics?.recent_adjustments || [];

  return (
    <div className="space-y-4">
      {/* Total Summary */}
      <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-orange-600 font-medium uppercase tracking-wide">Total Credits/Waivers</p>
            <p className="text-2xl font-bold text-orange-700">
              ₫{(summary.total_adjusted || 0).toLocaleString()}
            </p>
            <p className="text-xs text-orange-500">{summary.total_count || 0} adjustments</p>
          </div>
          <div className="p-3 bg-orange-100 rounded-xl">
            <DollarSign className="w-6 h-6 text-orange-600" />
          </div>
        </div>
      </div>

      {/* By Type Breakdown */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 bg-teal-50 rounded-xl border border-teal-100 text-center">
          <p className="text-lg font-bold text-teal-700">{summary.by_type?.credit?.count || 0}</p>
          <p className="text-[10px] text-teal-600 font-medium uppercase">Credits</p>
          <p className="text-xs text-teal-500">₫{(summary.by_type?.credit?.amount || 0).toLocaleString()}</p>
        </div>
        <div className="p-3 bg-green-50 rounded-xl border border-green-100 text-center">
          <p className="text-lg font-bold text-green-700">{summary.by_type?.waiver?.count || 0}</p>
          <p className="text-[10px] text-green-600 font-medium uppercase">Waivers</p>
          <p className="text-xs text-green-500">₫{(summary.by_type?.waiver?.amount || 0).toLocaleString()}</p>
        </div>
        <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 text-center">
          <p className="text-lg font-bold text-purple-700">{summary.by_type?.discount?.count || 0}</p>
          <p className="text-[10px] text-purple-600 font-medium uppercase">Discounts</p>
          <p className="text-xs text-purple-500">₫{(summary.by_type?.discount?.amount || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* By Service Breakdown */}
      {byService.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-stone-500">By Service</p>
          <div className="space-y-1">
            {byService.slice(0, 4).map((svc) => (
              <div key={svc.service_id} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                  <span className="text-sm text-stone-700">{svc.service_name}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-stone-800">₫{(svc.total_adjusted || 0).toLocaleString()}</span>
                  <span className="text-xs text-stone-400 ml-1">({svc.adjustment_count})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Adjustments */}
      {recentAdjustments.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-stone-500">Recent Credits</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {recentAdjustments.slice(0, 3).map((adj) => (
              <div key={adj.id} className="p-2 bg-orange-50/50 rounded-lg border border-orange-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs font-medium text-stone-700">
                      {adj.room_number} - {adj.building_name}
                    </p>
                    <p className="text-[10px] text-stone-500 truncate max-w-[150px]">{adj.reason}</p>
                  </div>
                  <span className="text-xs font-bold text-orange-600">
                    -₫{(adj.adjustment_amount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.total_count === 0 && (
        <div className="text-center py-4 text-stone-400 text-sm">
          No adjustments in this period
        </div>
      )}
    </div>
  );
};

// Widget Settings Modal Component - Enhanced with Glassmorphism
// Contract Expiry Tracker Widget
export const ContractExpiryWidget = () => {
  const { data: contracts, isLoading } = useQuery({
    queryKey: ['dashboard-contract-expiry'],
    queryFn: () => contractService.getContracts(),
    staleTime: 2 * 60 * 1000
  });

  const sorted = React.useMemo(() => {
    if (!Array.isArray(contracts)) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return contracts
      .filter(c => ['active', 'expiring_soon', 'expired'].includes(c.status))
      .map(c => {
        const end = new Date(c.end_date);
        end.setHours(0, 0, 0, 0);
        const days = Math.round((end - today) / (1000 * 60 * 60 * 24));
        return { ...c, daysLeft: days };
      })
      .sort((a, b) => a.daysLeft - b.daysLeft);
  }, [contracts]);

  const getBadge = (days, status) => {
    if (status === 'expired' || days < 0)
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', label: `${Math.abs(days)}d overdue` };
    if (days === 0)
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', label: 'Today' };
    if (days <= 7)
      return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', dot: 'bg-red-400', label: `${days}d left` };
    if (days <= 30)
      return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', label: `${days}d left` };
    return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100', dot: 'bg-emerald-400', label: `${days}d left` };
  };

  const getRowAccent = (days, status) => {
    if (status === 'expired' || days < 0) return 'border-l-red-400';
    if (days <= 7) return 'border-l-red-300';
    if (days <= 30) return 'border-l-amber-400';
    return 'border-l-emerald-300';
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="animate-pulse flex gap-3 p-2">
            <div className="w-10 h-10 bg-stone-200 rounded-xl flex-shrink-0"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-stone-200 rounded w-3/4"></div>
              <div className="h-3 bg-stone-200 rounded w-1/2"></div>
            </div>
            <div className="w-16 h-6 bg-stone-200 rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-stone-50 border border-stone-100 flex items-center justify-center mb-3">
          <FileText className="w-8 h-8 text-stone-300" />
        </div>
        <p className="text-sm font-medium text-stone-600">No active contracts</p>
        <p className="text-xs text-stone-400 mt-1">All contracts are terminated or in draft.</p>
      </div>
    );
  }

  const urgentCount = sorted.filter(c => c.daysLeft <= 30).length;
  const expiredCount = sorted.filter(c => c.daysLeft < 0 || c.status === 'expired').length;

  return (
    <div className="flex flex-col gap-3">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="flex flex-col items-center p-2 rounded-xl bg-stone-50 border border-stone-100">
          <p className="text-lg font-bold text-stone-800">{sorted.length}</p>
          <p className="text-[10px] text-stone-500 font-semibold uppercase tracking-wide">Total</p>
        </div>
        <div className="flex flex-col items-center p-2 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-lg font-bold text-amber-700">{urgentCount}</p>
          <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">≤ 30 days</p>
        </div>
        <div className="flex flex-col items-center p-2 rounded-xl bg-red-50 border border-red-100">
          <p className="text-lg font-bold text-red-700">{expiredCount}</p>
          <p className="text-[10px] text-red-500 font-semibold uppercase tracking-wide">Expired</p>
        </div>
      </div>

      {/* Contract list */}
      <div className="space-y-2 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
        {sorted.map(contract => {
          const badge = getBadge(contract.daysLeft, contract.status);
          const accent = getRowAccent(contract.daysLeft, contract.status);
          const primaryRenter = Array.isArray(contract.renters)
            ? contract.renters.find(r => r.is_primary) || contract.renters[0]
            : null;
          const tenantName = primaryRenter?.full_name || '—';

          return (
            <div
              key={contract.id}
              className={`p-3 rounded-xl border border-stone-100 border-l-4 ${accent} bg-white hover:shadow-md hover:shadow-teal-100/20 transition-all duration-200 group`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-stone-50 border border-stone-100 flex items-center justify-center flex-shrink-0 group-hover:border-teal-200 transition-colors">
                  <FileText className="w-4 h-4 text-stone-400 group-hover:text-teal-500 transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-stone-800 truncate">
                      {contract.room_number || `Room ${contract.room_id}`}
                    </p>
                    <span className="text-[10px] text-stone-400 font-mono truncate hidden sm:block">
                      #{contract.contract_number}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-xs text-stone-500 truncate">{tenantName}</p>
                    <span className="text-stone-300">·</span>
                    <p className="text-xs text-stone-400 flex-shrink-0">
                      {new Date(contract.end_date).toLocaleDateString('vi-VN')}
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full flex-shrink-0 border ${badge.bg} ${badge.border}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}></div>
                  <span className={`text-[10px] font-bold ${badge.text}`}>{badge.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WidgetSettingsModal = ({ widget, onSave, onClose }) => {
  const [settings, setSettings] = useState(widget.settings || {});
  const [size, setSize] = useState(widget.size || 'medium');

  const handleSave = () => {
    onSave(widget.id, { settings, size });
  };

  const sizeOptions = [
    { value: 'small', label: 'Small', description: '1 column width' },
    { value: 'medium', label: 'Medium', description: '2 columns width' },
    { value: 'large', label: 'Large', description: '3 columns width' },
    { value: 'full', label: 'Full Width', description: '4 columns width' }
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Widget Settings</h3>
            <p className="text-sm text-stone-500">Customize your widget appearance</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-stone-100 transition-colors"
          >
            <AlertCircle className="w-5 h-5 text-stone-400" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body space-y-6">
          {/* Widget Name */}
          <div className="p-4 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl border border-primary-100">
            <p className="text-sm text-stone-600">Widget</p>
            <p className="text-lg font-semibold text-stone-900">{widget.name || widget.type}</p>
          </div>

          {/* Size Selection */}
          <div className="space-y-3">
            <label className="form-label">Widget Size</label>
            <div className="grid grid-cols-2 gap-3">
              {sizeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSize(option.value)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${size === option.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20'
                      : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                    }`}
                >
                  <p className={`font-semibold ${size === option.value ? 'text-primary-700' : 'text-stone-900'}`}>
                    {option.label}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <label className="form-label">Preview</label>
            <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((col) => (
                  <div
                    key={col}
                    className={`h-8 rounded-lg transition-all ${(size === 'small' && col === 1) ||
                        (size === 'medium' && col <= 2) ||
                        (size === 'large' && col <= 3) ||
                        size === 'full'
                        ? 'bg-gradient-to-r from-primary-400 to-accent-400'
                        : 'bg-stone-200'
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <Button kind="secondary" onClick={onClose}>Cancel</Button>
          <Button kind="primary" onClick={handleSave} iconLeft={<CheckCircle size={16} />}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};

// ── Expense Summary Widget ────────────────────────────────────────────────────
export const ExpenseSummaryWidget = ({ timeRange }) => {
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['expenses-dashboard', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: async () => {
      const res = await fetch('/api/expenses', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!res.ok) return [];
      return res.json();
    },
    select: d => Array.isArray(d) ? d : [],
    staleTime: 60000
  });

  const summary = React.useMemo(() => {
    const filtered = expenses.filter(e => {
      const m = e.billing_month || e.expense_date?.substring(0, 7);
      if (!timeRange?.startMonth || !timeRange?.endMonth) return true;
      return m >= timeRange.startMonth && m <= timeRange.endMonth;
    });
    let total = 0;
    const byCategory = {};
    filtered.forEach(e => {
      const amt = parseFloat(e.amount || 0);
      total += amt;
      byCategory[e.category || 'Other'] = (byCategory[e.category || 'Other'] || 0) + amt;
    });
    return { total, count: filtered.length, byCategory: Object.entries(byCategory).sort((a, b) => b[1] - a[1]) };
  }, [expenses, timeRange]);

  if (isLoading) return (
    <div className="animate-pulse space-y-3">
      <div className="h-16 bg-stone-100 rounded-xl" />
      {[1, 2, 3].map(i => <div key={i} className="h-8 bg-stone-50 rounded-lg" />)}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-red-500 font-semibold uppercase tracking-wide">Total Expenses</p>
          <p className="text-2xl font-bold text-red-700">₫{summary.total.toLocaleString('vi-VN')}</p>
          <p className="text-xs text-red-400 mt-0.5">{summary.count} entries</p>
        </div>
        <TrendingDown className="h-10 w-10 text-red-200" />
      </div>
      {summary.byCategory.length > 0 ? (
        <div className="space-y-2.5">
          {summary.byCategory.slice(0, 5).map(([cat, amt]) => {
            const pct = summary.total > 0 ? (amt / summary.total) * 100 : 0;
            return (
              <div key={cat}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-stone-600 font-medium truncate max-w-[60%]">{cat}</span>
                  <span className="text-stone-700 font-semibold">₫{amt.toLocaleString('vi-VN')}</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-1.5">
                  <div className="bg-gradient-to-r from-red-400 to-rose-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {summary.byCategory.length > 5 && (
            <p className="text-xs text-stone-400 text-center">+{summary.byCategory.length - 5} more</p>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-stone-400">
          <Wallet className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No expenses in this period</p>
        </div>
      )}
    </div>
  );
};

// ── Service Usage Income vs Expense Widget ───────────────────────────────────
// Compares what renters paid for electric/water/internet vs actual expenses
export const IncomeVsExpenseWidget = ({ timeRange }) => {
  const { data: payments = [] } = useQuery({
    queryKey: ['payments-ivse', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getPayments({
      start_month: timeRange?.startMonth,
      end_month: timeRange?.endMonth
    }).then(d => Array.isArray(d) ? d : (d?.payments || [])),
    staleTime: 60000
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ['expenses-ivse', timeRange?.startMonth, timeRange?.endMonth],
    queryFn: () => financeService.getExpenses({
      start_date: timeRange?.startDate,
      end_date: timeRange?.endDate
    }),
    staleTime: 60000
  });

  // Map expense categories to service keys
  const EXPENSE_CATEGORY_MAP = {
    electric: ['electric', 'electricity', 'dien', 'điện'],
    water: ['water', 'nuoc', 'nước'],
    internet: ['internet', 'wifi', 'mang', 'mạng'],
  };

  const matchesService = (category, service) => {
    const cat = (category || '').toLowerCase();
    return EXPENSE_CATEGORY_MAP[service]?.some(k => cat.includes(k)) ?? false;
  };

  const data = React.useMemo(() => {
    const inRange = (m) => {
      if (!timeRange?.startMonth || !timeRange?.endMonth) return true;
      return m >= timeRange.startMonth && m <= timeRange.endMonth;
    };

    // Income from confirmed/partial payments
    let electricIncome = 0, waterIncome = 0, internetIncome = 0;
    payments.forEach(p => {
      if (!inRange(p.month) || !['confirmed', 'partial'].includes(p.status)) return;
      electricIncome += parseFloat(p.electric_cost || 0);
      waterIncome += parseFloat(p.water_cost || 0);
      // Internet from other_costs_detail
      try {
        const detail = typeof p.other_costs_detail === 'string'
          ? JSON.parse(p.other_costs_detail) : (p.other_costs_detail || {});
        const entries = Array.isArray(detail) ? detail : Object.values(detail);
        entries.forEach(svc => {
          const name = (svc.name || svc.service_id || '').toLowerCase();
          if (name.includes('internet') || name.includes('wifi') || name.includes('mạng')) {
            internetIncome += parseFloat(svc.price || 0);
          }
        });
      } catch {}
    });

    // Expenses by service category
    let electricExp = 0, waterExp = 0, internetExp = 0;
    expenses.forEach(e => {
      const m = e.billing_month || e.expense_date?.substring(0, 7);
      if (!inRange(m)) return;
      const amt = parseFloat(e.amount || 0);
      if (matchesService(e.category, 'electric')) electricExp += amt;
      else if (matchesService(e.category, 'water')) waterExp += amt;
      else if (matchesService(e.category, 'internet')) internetExp += amt;
    });

    const services = [
      {
        key: 'electric', label: 'Electric',
        income: electricIncome, expense: electricExp,
        incomeColor: 'bg-yellow-400', expenseColor: 'bg-yellow-200',
        icon: '⚡',
      },
      {
        key: 'water', label: 'Water',
        income: waterIncome, expense: waterExp,
        incomeColor: 'bg-teal-400', expenseColor: 'bg-teal-200',
        icon: '💧',
      },
      {
        key: 'internet', label: 'Internet',
        income: internetIncome, expense: internetExp,
        incomeColor: 'bg-purple-400', expenseColor: 'bg-purple-200',
        icon: '📡',
      },
    ];

    return services;
  }, [payments, expenses, timeRange]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs text-stone-500 mb-2">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Collected from renters</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block" /> Actual expense</span>
      </div>

      {data.map(svc => {
        const max = Math.max(svc.income, svc.expense, 1);
        const incPct = (svc.income / max) * 100;
        const expPct = (svc.expense / max) * 100;
        const net = svc.income - svc.expense;
        return (
          <div key={svc.key} className="bg-stone-50 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-stone-700">{svc.icon} {svc.label}</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${net >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {net >= 0 ? '+' : ''}₫{net.toLocaleString('vi-VN')}
              </span>
            </div>
            <div className="space-y-1.5">
              <div>
                <div className="flex justify-between text-[10px] text-stone-500 mb-0.5">
                  <span>Collected</span>
                  <span>₫{svc.income.toLocaleString('vi-VN')}</span>
                </div>
                <div className="w-full bg-stone-200 rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all duration-500" style={{ width: `${incPct}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] text-stone-500 mb-0.5">
                  <span>Expense</span>
                  <span>₫{svc.expense.toLocaleString('vi-VN')}</span>
                </div>
                <div className="w-full bg-stone-200 rounded-full h-2">
                  <div className="bg-red-400 h-2 rounded-full transition-all duration-500" style={{ width: `${expPct}%` }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
